import { SystemAccessTabProps } from "./SystemAccessTab.tsx";
import { UserAccessOrigin, UserAccessViewModel, UsersAccessViewModel } from "./SystemAccessTab.types.ts";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { useState } from "react";
import { setFirstLetterToLowerCase } from "../../../common/stringOperations.ts";
import { FilterOption, FilterOptionType } from "@common/components/filterPopover/FilterPopover.types.ts";
import { getUniqueFilterOptions, sortByName } from "@common/hooks/filterUtils.ts";
import { useFilterState } from "@common/hooks/useFilterState.ts";
import { applyClientFilters, withOperators } from "@common/components/filterBar/filterOperatorMatching.ts";

import { useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";

export function useSystemAccessTab(props: SystemAccessTabProps) {
    const { translateString } = useTranslation();

    const [showGrantAccessDialog, setShowGrantAccessDialog] = useState<UserAccessViewModel | undefined>(undefined);
    const [showExcludeAccessDialog, setShowExcludeAccessDialog] = useState<UserAccessViewModel | undefined>(undefined);
    const {
        selectFilters: filters,
        filterOperators,
        onFilterChange,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
    } = useFilterState(`system-${props.systemId}-access-filters`);

    const url = `/system-user-access/${props.systemId}`;
    const { isLoading, data, refetch } = useDotLegalQuery<UsersAccessViewModel>(url);

    function getMenuItems(model: UserAccessViewModel) {
        let menuItems = [];

        if (!model.accessOrigins.includes(UserAccessOrigin.ContractManager)) {
            menuItems.push({
                menuItem: model.hasAccess ? translateString("exclude") : translateString("removeFromExcluded"),
                onClick: () => (model.hasAccess ? setShowExcludeAccessDialog(model) : setShowGrantAccessDialog(model)),
            });
        }

        return menuItems;
    }

    function getTableData(data: Array<UserAccessViewModel>) {
        return data.map((x) => {
            return {
                ...x,
                hasAccessString: x.hasAccess ? translateString("granted") : translateString("excluded"),
                accessOriginsString: x.accessOrigins
                    .filter((x) => x !== UserAccessOrigin.AccessExcluded)
                    .map((x) => translateString(setFirstLetterToLowerCase(UserAccessOrigin[x].toString())))
                    .join(", "),
            };
        });
    }

    const allRows = getTableData(data?.users ?? []);

    const filteredRows = applyClientFilters(
        allRows,
        [
            { id: "name", candidate: (r) => r.id },
            { id: "email", candidate: (r) => r.email },
            { id: "accessOrigin", candidate: (r) => r.accessOrigins.filter((x) => x !== UserAccessOrigin.AccessExcluded).map((x) => x.toString()) },
            { id: "access", candidate: (r) => (r.hasAccess ? "1" : "0") },
        ],
        filters,
        {},
        filterOperators
    )!;

    function getFilterOptions(): Array<FilterOption> {
        return withOperators(
            [
                {
                    id: "name",
                    label: translateString("name"),
                    type: FilterOptionType.SelectField,
                    value: filters.name ?? [],
                    fixedData: getUniqueFilterOptions(
                        allRows,
                        (r) => r.fullName,
                        (r) => r.id
                    ).sort(sortByName),
                },
                {
                    id: "email",
                    label: translateString("email"),
                    type: FilterOptionType.SelectField,
                    value: filters.email ?? [],
                    fixedData: getUniqueFilterOptions(allRows, (r) => r.email).sort(sortByName),
                },
                {
                    id: "accessOrigin",
                    label: translateString("accessGrantedFrom"),
                    type: FilterOptionType.SelectField,
                    value: filters.accessOrigin ?? [],
                    fixedData: [
                        UserAccessOrigin.ContractManager,
                        UserAccessOrigin.AdditionalAccess,
                        UserAccessOrigin.BusinessAreaAccess,
                        UserAccessOrigin.AccessibleToAllUsers,
                        UserAccessOrigin.Responsible,
                    ].map((origin) => ({
                        id: origin.toString(),
                        name: translateString(setFirstLetterToLowerCase(UserAccessOrigin[origin].toString())),
                    })),
                },
                {
                    id: "access",
                    label: translateString("access"),
                    type: FilterOptionType.SelectField,
                    value: filters.access ?? [],
                    fixedData: [
                        { id: "1", name: translateString("granted") },
                        { id: "0", name: translateString("excluded") },
                    ],
                },
            ],
            filterOperators
        );
    }

    const hasActiveFilters = Object.values(filters).some((v) => v.length > 0);

    const onAccessExcluded = () => {
        setShowExcludeAccessDialog(undefined);
        refetch();
    };

    const onRemoveFromExcludedList = () => {
        setShowGrantAccessDialog(undefined);
        refetch();
    };

    const onCloseDialog = () => {
        setShowGrantAccessDialog(undefined);
        setShowExcludeAccessDialog(undefined);
    };

    return {
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        isLoading,
        rows: filteredRows,
        accessStepOrder: data?.accessStepOrder,
        canNavigateToAccessStep: data?.canNavigateToAccessStep ?? false,
        canManageExcludedUsers: data?.canManageExcludedUsers ?? false,
        getMenuItems,
        showGrantAccessDialog,
        showExcludeAccessDialog,
        onAccessExcluded,
        onRemoveFromExcludedList,
        onCloseDialog,
        getFilterOptions,
        onFilterChange,
        hasActiveFilters,
    };
}
